import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaBalanceScale,
  FaChevronLeft,
  FaChevronRight,
  FaImage,
  FaTimes,
} from "react-icons/fa";

const text = (v) => {
  if (v === 0 || v === "0") return "0";
  if (!v) return "";

  if (typeof v === "string" || typeof v === "number") return String(v);

  if (Array.isArray(v)) {
    return v.map(text).filter(Boolean).join(", ");
  }

  if (typeof v === "object") {
    return (
      v.label ||
      v.name ||
      v.title ||
      v.itemName ||
      v.itemCode ||
      v.ItemCode ||
      v.value ||
      ""
    );
  }

  return String(v);
};

const hasValue = (v) => {
  const t = text(v);
  return t !== "" && t !== "undefined" && t !== "null";
};

const hasPositiveValue = (v) => {
  const n = parseFloat(text(v));
  return Number.isFinite(n) && n > 0;
};

const money = (v) => {
  const n = parseFloat(text(v));
  if (!Number.isFinite(n)) return "—";
  return `₹ ${n.toFixed(2)}`;
};

const gram = (v) => {
  const n = parseFloat(text(v));
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(3)} g`;
};

const percent = (v) => {
  const n = parseFloat(text(v));
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)}%`;
};

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";

  const clean = url.trim();
  if (!clean) return "";

  if (
    clean.startsWith("http://") ||
    clean.startsWith("https://") ||
    clean.startsWith("data:") ||
    clean.startsWith("blob:") ||
    clean.startsWith("/")
  ) {
    return clean;
  }

  return `/${clean.replace(/^\.?\//, "")}`;
};

const extractImages = (item) => {
  const output = [];
  const seen = new Set();

  const push = (value) => {
    if (!value) return;

    if (Array.isArray(value)) {
      value.forEach(push);
      return;
    }

    if (typeof value === "string") {
      const finalUrl = normalizeImageUrl(value);
      if (finalUrl && !seen.has(finalUrl)) {
        seen.add(finalUrl);
        output.push(finalUrl);
      }
      return;
    }

    if (typeof value === "object") {
      push(
        value.url ||
          value.imageUrl ||
          value.ImageUrl ||
          value.image ||
          value.src ||
          value.path ||
          value.fileUrl ||
          value.thumbnail,
      );
    }
  };

  const scan = (obj, depth = 0) => {
    if (!obj || typeof obj !== "object" || depth > 2) return;

    Object.entries(obj).forEach(([key, value]) => {
      const k = key.toLowerCase();

      if (
        k.includes("image") ||
        k.includes("photo") ||
        k.includes("thumb") ||
        k === "url" ||
        k === "src"
      ) {
        push(value);
      }

      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        (k.includes("info") ||
          k.includes("detail") ||
          k.includes("product") ||
          k.includes("sku") ||
          k.includes("item"))
      ) {
        scan(value, depth + 1);
      }
    });
  };

  scan(item);

  return output;
};

const validInvoiceItem = (item) => {
  if (!item) return false;

  return [
    "itemCodeDetail",
    "itemCode",
    "category",
    "product",
    "design",
    "purity",
    "stnPopupName",
    "diaName",
    "totalWt",
    "grossWt",
    "netWt",
    "itemAmount",
    "totalItemAmount",
    "mrp",
  ].some((key) => hasValue(item?.[key]));
};

const getTitle = (item, index) =>
  text(item?.itemCodeDetail) ||
  text(item?.itemCode) ||
  text(item?.ItemCode) ||
  text(item?.stnPopupName) ||
  text(item?.diaName) ||
  text(item?.product) ||
  `Item ${index + 1}`;

const buildCompareItems = (
  stockEntries = [],
  looseStones = [],
  looseDiamonds = [],
) => {
  const ornaments = (stockEntries || [])
    .filter(validInvoiceItem)
    .map((item, index) => ({
      type: "Ornament",
      raw: item,
      title: getTitle(item, index),
      images: extractImages(item),
    }));

  const stones = (looseStones || [])
    .filter(validInvoiceItem)
    .map((item, index) => ({
      type: "Loose Stone",
      raw: item,
      title: getTitle(item, index),
      images: extractImages(item),
    }));

  const diamonds = (looseDiamonds || [])
    .filter(validInvoiceItem)
    .map((item, index) => ({
      type: "Loose Diamond",
      raw: item,
      title: getTitle(item, index),
      images: extractImages(item),
    }));

  return [...ornaments, ...stones, ...diamonds];
};

const METRICS = [
  {
    label: "Category",
    get: (item) => text(item?.category) || "—",
    show: (items) => items.some((i) => hasValue(i?.category)),
  },
  {
    label: "Product",
    get: (item) => text(item?.product) || "—",
    show: (items) => items.some((i) => hasValue(i?.product)),
  },
  {
    label: "Design",
    get: (item) => text(item?.design) || "—",
    show: (items) => items.some((i) => hasValue(i?.design)),
  },
  {
    label: "Purity",
    get: (item) => text(item?.purity) || "—",
    show: (items) => items.some((i) => hasValue(i?.purity)),
  },
  {
    label: "Total Wt",
    get: (item) => gram(item?.totalWt),
    show: (items) => items.some((i) => hasValue(i?.totalWt)),
    strong: true,
  },
  {
    label: "Stone Wt",
    get: (item) => gram(item?.stoneWt),
    show: (items) => items.some((i) => hasValue(i?.stoneWt)),
  },
  {
    label: "Dia Wt",
    get: (item) => gram(item?.diamondWt),
    show: (items) => items.some((i) => hasValue(i?.diamondWt)),
  },
  {
    label: "Net Wt",
    get: (item) => gram(item?.netWt),
    show: (items) => items.some((i) => hasValue(i?.netWt)),
    strong: true,
  },
  {
    label: "Rate / gm",
    get: (item) => money(item?.ratePerGram),
    show: (items) => items.some((i) => hasValue(i?.ratePerGram)),
  },
  {
    label: "Making %",
    get: (item) => percent(item?.makingPercentage),
    show: (items) => items.some((i) => hasPositiveValue(i?.makingPercentage)),
  },
  {
    label: "Making / gm",
    get: (item) => money(item?.makingPerGram),
    show: (items) => items.some((i) => hasPositiveValue(i?.makingPerGram)),
  },
  {
    label: "Fixed Wastage",
    get: (item) => gram(item?.fixedWastage),
    show: (items) => items.some((i) => hasPositiveValue(i?.fixedWastage)),
  },
  {
    label: "Fixed Amt",
    get: (item) => money(item?.fixedAmount),
    show: (items) => items.some((i) => hasPositiveValue(i?.fixedAmount)),
  },
  {
    label: "Hallmark",
    get: (item) => money(item?.hallmarkAmount),
    show: (items) => items.some((i) => hasPositiveValue(i?.hallmarkAmount)),
  },
  {
    label: "Item Amt",
    get: (item) => money(item?.itemAmount),
    show: (items) => items.some((i) => hasValue(i?.itemAmount)),
  },
  {
    label: "Discount",
    get: (item) => money(item?.itemDiscountAmount),
    show: (items) => items.some((i) => hasPositiveValue(i?.itemDiscountAmount)),
  },
  {
    label: "Tax",
    get: (item) => money(item?.itemGstAmount),
    show: (items) => items.some((i) => hasValue(i?.itemGstAmount)),
  },
  {
    label: "Final",
    get: (item) => money(item?.totalItemAmount || item?.mrp),
    show: (items) =>
      items.some((i) => hasValue(i?.totalItemAmount) || hasValue(i?.mrp)),
    strong: true,
    highlight: true,
  },
  {
    label: "MRP",
    get: (item) => money(item?.mrp),
    show: (items) => items.some((i) => hasPositiveValue(i?.mrp)),
    strong: true,
    highlight: true,
  },
];

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.985 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 280,
      damping: 26,
      mass: 0.8,
      staggerChildren: 0.035,
    },
  },
  exit: {
    opacity: 0,
    y: 18,
    scale: 0.985,
    transition: { duration: 0.14 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

const headerItemVariants = {
  hidden: { opacity: 0, y: -8 },
  visible: { opacity: 1, y: 0 },
};

export default function InvoiceItemCompareModal({
  stockEntries = [],
  looseStones = [],
  looseDiamonds = [],
}) {
  const [open, setOpen] = useState(false);

  const allItems = useMemo(
    () => buildCompareItems(stockEntries, looseStones, looseDiamonds),
    [stockEntries, looseStones, looseDiamonds],
  );

  const compareItems = useMemo(() => allItems.slice(0, 3), [allItems]);

  useEffect(() => {
    const onKey = (e) => {
      const isCompareShortcut =
        e.ctrlKey &&
        e.altKey &&
        !e.shiftKey &&
        String(e.key || "").toLowerCase() === "c";

      if (!isCompareShortcut) return;
      if (allItems.length < 2) return;

      e.preventDefault();
      e.stopPropagation();

      setOpen(true);
    };

    window.addEventListener("keydown", onKey, true);

    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [allItems.length]);

  if (allItems.length < 2) return null;

  return (
    <>
      {/* <motion.button
  whileHover={{ scale: 1.08, y: -1 }}
  whileTap={{ scale: 0.94, y: 0 }}
  type="button"
  className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[11px] text-slate-600 hover:text-violet-700 hover:bg-violet-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
  aria-label="Compare invoice items"
>
  
</motion.button> */}

      <button
        type="button"
        title={`Compare items (${compareItems.length}/3) - Ctrl + Alt + C`}
        onClick={() => setOpen(true)}
        className="inline-flex h-7 w-7 ml-1 items-center justify-center rounded-lg text-[11px] text-slate-600 hover:text-violet-700 hover:bg-violet-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
        // aria-expanded={sumOpen}
        aria-haspopup="dialog"
      >
        <FaBalanceScale size={18} />
      </button>

      <AnimatePresence>
        {open && (
          <ComparisonModal
            items={compareItems}
            totalItems={allItems.length}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function ComparisonModal({ items = [], totalItems = 0, onClose }) {
  const closeButtonRef = useRef(null);
  const focusTimerRef = useRef(null);

  const focusCloseButton = useCallback(() => {
    if (focusTimerRef.current) {
      clearTimeout(focusTimerRef.current);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        focusTimerRef.current = setTimeout(() => {
          const btn = closeButtonRef.current;
          if (!btn) return;

          btn.focus({ preventScroll: true });
        }, 30);
      });
    });
  }, []);

  useEffect(() => {
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    focusCloseButton();

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
        return;
      }

      if (
        e.key === "Enter" &&
        document.activeElement === closeButtonRef.current
      ) {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKey, true);

    return () => {
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
      }

      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", onKey, true);
    };
  }, [focusCloseButton, onClose]);

  const visibleMetrics = useMemo(() => {
    const raws = items.map((x) => x.raw || {});
    return METRICS.filter((m) => m.show(raws));
  }, [items]);

  const gridTemplateColumns = `104px repeat(${items.length}, minmax(0, 1fr))`;

  return (
    <motion.div
      className="fixed inset-0 z-[3000] flex items-end sm:items-center justify-center bg-slate-950/55 backdrop-blur-md px-2 sm:px-4 py-3"
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onAnimationComplete={focusCloseButton}
        className="w-full max-w-5xl max-h-[94vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-[0_30px_90px_rgba(15,23,42,0.35)] overflow-hidden flex flex-col border border-white/60"
        role="dialog"
        aria-modal="true"
        aria-label="Invoice item comparison"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-[#9481ce] text-white px-3 sm:px-4 py-3 flex items-center justify-between gap-3">
          <motion.div
            className="absolute -top-20 -right-20 h-44 w-44 rounded-full bg-white/15 blur-2xl"
            animate={{ scale: [1, 1.12, 1], opacity: [0.45, 0.75, 0.45] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />

          <motion.div
            className="absolute -bottom-24 -left-16 h-44 w-44 rounded-full bg-violet-300/25 blur-2xl"
            animate={{ scale: [1.08, 1, 1.08], opacity: [0.35, 0.65, 0.35] }}
            transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
          />

          <div className="relative flex items-center gap-2 min-w-0">
            <motion.span
              className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20"
              initial={{ rotate: -8, scale: 0.9 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 18 }}
            >
              <FaBalanceScale className="text-sm" />
            </motion.span>

            <div className="min-w-0">
              <h3 className="text-sm sm:text-lg font-semibold tracking-tight truncate">
                Compare Items
              </h3>
              <p className="text-[10px] sm:text-[11px] text-violet-50 truncate">
                Showing {items.length} of {totalItems} invoice items
              </p>
            </div>
          </div>

          <motion.button
            ref={closeButtonRef}
            type="button"
            tabIndex={0}
            autoFocus
            onClick={onClose}
            whileHover={{ scale: 1.06, rotate: 90 }}
            whileTap={{ scale: 0.92 }}
            className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 ring-1 ring-white/15 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#9481ce]"
            title="Close"
            aria-label="Close comparison modal"
          >
            <FaTimes className="text-xs" />
          </motion.button>
        </div>

        {/* Desktop + Tablet professional comparison matrix */}
        <div className="hidden sm:block p-3 bg-gradient-to-br from-slate-50 via-white to-violet-50/40">
          <motion.div
            variants={rowVariants}
            className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-xl shadow-slate-200/50"
          >
            <div
              className="grid border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white"
              style={{ gridTemplateColumns }}
            >
              <div className="px-3 py-2 text-[11px] font-bold text-slate-500 flex items-center">
                Compare
              </div>

              {items.map((item, index) => (
                <motion.div
                  key={`${item.title}-${index}`}
                  variants={headerItemVariants}
                  whileHover={{ backgroundColor: "rgba(248, 250, 252, 1)" }}
                  className="px-2 py-2 border-l border-slate-100 min-w-0"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <CompactImageCarousel
                      images={item.images}
                      title={item.title}
                    />

                    <div className="min-w-0">
                      <span className="inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-[9px] font-bold text-[#6b59c6] ring-1 ring-violet-100">
                        Item {index + 1}
                      </span>

                      <p className="mt-0.5 text-[12px] font-bold text-slate-900 truncate">
                        {item.title}
                      </p>

                      <p className="text-[10px] font-medium text-slate-400 truncate">
                        {item.type}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {visibleMetrics.map((metric, rowIndex) => (
              <motion.div
                key={metric.label}
                variants={rowVariants}
                custom={rowIndex}
                whileHover={{
                  backgroundColor: metric.highlight
                    ? "rgba(236, 253, 245, 0.95)"
                    : "rgba(248, 250, 252, 0.9)",
                }}
                className={`grid border-b border-slate-100 last:border-b-0 ${
                  metric.highlight
                    ? "bg-gradient-to-r from-emerald-50 to-white"
                    : rowIndex % 2 === 0
                      ? "bg-white"
                      : "bg-slate-50/45"
                }`}
                style={{ gridTemplateColumns }}
              >
                <div
                  className={`px-3 py-1.5 text-[11px] font-semibold flex items-center ${
                    metric.highlight ? "text-emerald-700" : "text-slate-500"
                  }`}
                >
                  {metric.label}
                </div>

                {items.map((item, index) => (
                  <div
                    key={`${metric.label}-${index}`}
                    className={`px-2 py-1.5 border-l border-slate-100 text-center text-[11px] truncate ${
                      metric.strong
                        ? metric.highlight
                          ? "font-extrabold text-emerald-700"
                          : "font-bold text-slate-900"
                        : "font-semibold text-slate-700"
                    }`}
                    title={metric.get(item.raw)}
                  >
                    {metric.highlight ? (
                      <span className="inline-flex items-center justify-center rounded-full bg-white/70 px-2 py-0.5 ring-1 ring-emerald-100">
                        {metric.get(item.raw)}
                      </span>
                    ) : (
                      metric.get(item.raw)
                    )}
                  </div>
                ))}
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Mobile compact stacked view */}
        <div className="sm:hidden flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-violet-50/50 p-2">
          <motion.div
            className="space-y-2"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.06,
                },
              },
            }}
          >
            {items.map((item, index) => (
              <MobileCompareCard
                key={`${item.title}-${index}`}
                item={item}
                index={index}
                metrics={visibleMetrics}
              />
            ))}
          </motion.div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t bg-white px-3 py-2">
          <span className="text-[10px] text-slate-500">
            Clean customer comparison view.
          </span>

          <motion.button
            type="button"
            onClick={onClose}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 shadow-sm"
          >
            Close
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function CompactImageCarousel({ images = [], title = "" }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
  }, [images?.length]);

  if (!images.length) {
    return (
      <motion.div
        whileHover={{ scale: 1.04 }}
        className="h-14 w-14 shrink-0 rounded-2xl border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300"
      >
        <FaImage className="text-lg" />
      </motion.div>
    );
  }

  const current = images[Math.min(active, images.length - 1)];

  return (
    <motion.div
      whileHover={{ scale: 1.04 }}
      className="relative h-14 w-14 shrink-0 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 shadow-sm"
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={current}
          src={current}
          alt={title || "Item image"}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.18 }}
        />
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={() =>
              setActive((p) => (p - 1 + images.length) % images.length)
            }
            className="absolute left-0.5 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-white/90 text-slate-700 shadow flex items-center justify-center"
          >
            <FaChevronLeft className="text-[8px]" />
          </button>

          <button
            type="button"
            onClick={() => setActive((p) => (p + 1) % images.length)}
            className="absolute right-0.5 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-white/90 text-slate-700 shadow flex items-center justify-center"
          >
            <FaChevronRight className="text-[8px]" />
          </button>

          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-black/45 px-1.5 py-0.5 text-[8px] font-bold text-white">
            {active + 1}/{images.length}
          </span>
        </>
      )}
    </motion.div>
  );
}

function MobileCompareCard({ item, index, metrics = [] }) {
  return (
    <motion.div
      variants={rowVariants}
      whileHover={{ y: -1 }}
      className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-lg shadow-slate-200/60"
    >
      <div className="p-2 flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-white to-violet-50/50">
        <CompactImageCarousel images={item.images} title={item.title} />

        <div className="min-w-0 flex-1">
          <span className="inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-[9px] font-bold text-[#6b59c6] ring-1 ring-violet-100">
            Item {index + 1}
          </span>

          <p className="mt-0.5 text-[12px] font-bold text-slate-900 truncate">
            {item.title}
          </p>

          <p className="text-[10px] font-medium text-slate-400 truncate">
            {item.type}
          </p>
        </div>
      </div>

      <div>
        {metrics.map((metric, rowIndex) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: rowIndex * 0.015 }}
            className={`flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-slate-50 last:border-b-0 text-[11px] ${
              metric.highlight
                ? "bg-gradient-to-r from-emerald-50 to-white"
                : rowIndex % 2 === 0
                  ? "bg-white"
                  : "bg-slate-50/55"
            }`}
          >
            <span
              className={`font-semibold ${
                metric.highlight ? "text-emerald-700" : "text-slate-500"
              }`}
            >
              {metric.label}
            </span>

            <span
              className={`text-right truncate ${
                metric.strong
                  ? metric.highlight
                    ? "font-extrabold text-emerald-700"
                    : "font-bold text-slate-900"
                  : "font-semibold text-slate-700"
              }`}
            >
              {metric.highlight ? (
                <span className="inline-flex rounded-full bg-white/80 px-2 py-0.5 ring-1 ring-emerald-100">
                  {metric.get(item.raw)}
                </span>
              ) : (
                metric.get(item.raw)
              )}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
